import React, { useEffect, useState } from "react";
import { User, Phone, Mail, Award, X } from "lucide-react";
import { createPortal } from "react-dom";
import { IoIosArrowDown } from "react-icons/io";
import axios from "axios";

const AddClientModal = ({ onClose, onSubmit }) => {
  const [isoDropdown, setIsoDropdown] = useState(false);
  const [complianceDropdown, setComplianceDropdown] = useState(false);
  const [isoCertificate, setIsoCertificate] = useState([]);
  const [complianceCertificate, setComplianceCertificate] = useState([]);
  const [formData, setFormData] = useState({
    clientName: "",
    phone: "",
    email: "",
    servicesTaken: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCertificateToggle = (e) => {
    const { value, checked } = e.target;
    setFormData((prev) => {
      const updated = checked
        ? [
            ...prev.servicesTaken,
            { certificateId: value, issuedDate: "", expiryDate: "" },
          ]
        : prev.servicesTaken.filter((c) => c.certificateId !== value);
      return { ...prev, servicesTaken: updated };
    });
  };

  const handleDateChange = (id, field, value) => {
    setFormData((prev) => ({
      ...prev,
      servicesTaken: prev.servicesTaken.map((c) =>
        c.certificateId === id ? { ...c, [field]: value } : c
      ),
    }));
  };

  const fetchCertificates = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/certificate/get-certificates`
      );
      const data = res?.data || [];
      setIsoCertificate(
        data.filter((c) => c.certificateType === "ISO Certificate")
      );
      setComplianceCertificate(
        data.filter((c) => c.certificateType === "Compliance Certificate")
      );
    } catch {
      setError("Failed to fetch certificates");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = `${process.env.REACT_APP_API_URL}/our-client/apply-client`;
      const res = await axios.post(url, formData, {
        headers: { "Content-Type": "application/json" },
      });
      onSubmit(res.data);
      onClose();
    } catch {
      setError("Failed to submit client");
    }
  };

  useEffect(() => {
    fetchCertificates();
  }, []);

  const renderDateInputs = (id) => {
    const cert = formData.servicesTaken.find((s) => s.certificateId === id);
    if (!cert) return null;
    return (
      <div className="flex flex-col gap-2 ml-6 mt-2">
        <input
          type="date"
          value={cert.issuedDate?.slice(0, 10) || ""}
          onChange={(e) => handleDateChange(id, "issuedDate", e.target.value)}
          className="border rounded-md px-2 py-1 text-sm text-gray-700"
          required
        />
        <input
          type="date"
          value={cert.expiryDate?.slice(0, 10) || ""}
          onChange={(e) => handleDateChange(id, "expiryDate", e.target.value)}
          className="border rounded-md px-2 py-1 text-sm text-gray-700"
          required
        />
      </div>
    );
  };

  const renderCertificateSection = (
    title,
    certificates,
    dropdownState,
    setDropdown
  ) => (
    <div
      onMouseEnter={() => setDropdown(true)}
      onMouseLeave={() => setDropdown(false)}
      className="relative"
    >
      <span className="flex gap-1 text-sm text-gray-600 cursor-pointer p-3 rounded-lg hover:bg-white">
        {title} <IoIosArrowDown size={16} />
      </span>
      <div
        className={`transition-all duration-300 overflow-hidden ${
          dropdownState
            ? "max-h-96 opacity-100 scale-100"
            : "max-h-0 opacity-0 scale-95"
        }`}
      >
        <div className="ml-2 flex flex-col gap-2 py-2">
          {certificates.map((type, idx) => (
            <div key={idx}>
              <label className="flex items-center gap-2 p-2 hover:bg-white">
                <input
                  type="checkbox"
                  value={type._id}
                  checked={formData.servicesTaken.some(
                    (c) => c.certificateId === type._id
                  )}
                  onChange={handleCertificateToggle}
                  className="w-4 h-4 text-blue-500"
                />
                <span>{type.certificateName}</span>
              </label>
              {renderDateInputs(type._id)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="max-w-3xl w-full bg-white rounded-xl shadow-xl overflow-hidden max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <div className="py-1 bg-gradient-to-r from-[#0070a8] to-[#023955] shadow-lg relative">
            <p className="text-center text-xl font-normal p-2 text-white">
              Add Client
            </p>
            <button
              type="button"
              onClick={onClose}
              className="absolute top-4 right-4 text-white hover:text-gray-300"
            >
              <X size={24} />
            </button>
          </div>

          <div className="p-6 md:p-8 space-y-5">
            <div className="rounded-xl bg-gray-50 px-4 py-3 flex items-center gap-3 border border-gray-200">
              <User size={18} className="text-gray-400" />
              <input
                type="text"
                name="clientName"
                value={formData.clientName}
                onChange={handleChange}
                className="w-full bg-transparent outline-none text-gray-700 text-sm md:text-base"
                placeholder="Enter Your Name"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-xl bg-gray-50 px-4 py-3 flex items-center gap-3 border border-gray-200">
                <Phone size={18} className="text-gray-400" />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full bg-transparent outline-none text-gray-700 text-sm md:text-base"
                  placeholder="Mobile Number"
                  required
                />
              </div>

              <div className="rounded-xl bg-gray-50 px-4 py-3 flex items-center gap-3 border border-gray-200">
                <Mail size={18} className="text-gray-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full bg-transparent outline-none text-gray-700 text-sm md:text-base"
                  placeholder="Email Address"
                  required
                />
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl px-4 py-3 border border-gray-200">
              <div className="flex gap-2 items-center mb-2">
                <Award size={20} className="text-blue-500" />
                <h2 className="text-gray-500 text-sm md:text-base">
                  Certificates
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {renderCertificateSection(
                  "ISO Certificate",
                  isoCertificate,
                  isoDropdown,
                  setIsoDropdown
                )}
                {renderCertificateSection(
                  "Compliance Certificate",
                  complianceCertificate,
                  complianceDropdown,
                  setComplianceDropdown
                )}
              </div>
            </div>

            <div className="flex gap-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gradient-to-r from-[#333333] to-[#cccccc] text-white py-2 rounded-lg text-sm font-medium shadow-md hover:shadow-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 bg-gradient-to-r from-[#0070a8] to-[#023955] text-white py-2 rounded-lg text-sm font-medium shadow-md hover:shadow-lg"
              >
                Submit
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default AddClientModal;
